import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChangeList } from "../components/change-list";
import { B2B_OPERATIONS, type B2bOperation, type B2bWriteMode } from "../../../../../../shared/index";

/**
 * The change log — every B2B change across clients, catalog and settings.
 *
 * It exists because the notification email has to lead somewhere. An email saying "3
 * grants removed from TR_X" is not an audit trail on its own; the record it links to is,
 * and that record has to be reachable by someone who was not the one who made the change.
 *
 * `/dashboard/b2b/changes/:id` renders the single record the email links to; the same
 * component handles both, so the row looks identical wherever it is read.
 */

const MODES: B2bWriteMode[] = ["live", "dry-run", "readonly"];
const PAGE_SIZE = 50;

export default function B2bChanges() {
  const { id } = useParams<{ id?: string }>();
  return id ? <SingleChange id={id} /> : <ChangeFeed />;
}

function SingleChange({ id }: { id: string }) {
  const change = useQuery({ queryKey: ["b2b", "change", id], queryFn: () => api.b2b.change(id) });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Change record</h1>
        <p className="text-sm text-muted-foreground">
          The record a notification email links to
        </p>
      </div>

      {change.isError && (
        <p className="text-sm text-destructive">
          This change could not be found. It may have been recorded before this tool started
          keeping them.
        </p>
      )}

      <div className="rounded-xl border bg-card p-2">
        <ChangeList
          changes={change.data ? [change.data] : []}
          loading={change.isLoading}
          emptyMessage="No such change."
        />
      </div>

      <Button variant="ghost" asChild>
        <a href="/dashboard/b2b/changes">← All changes</a>
      </Button>
    </div>
  );
}

function ChangeFeed() {
  const [params, setParams] = useSearchParams();
  const [actorInput, setActorInput] = useState(params.get("actor") ?? "");

  const page = Number(params.get("page") ?? 1);
  const filters = useMemo(
    () => ({
      actor: params.get("actor") || undefined,
      operation: (params.get("operation") as B2bOperation | null) || undefined,
      mode: (params.get("mode") as B2bWriteMode | null) || undefined,
      from: params.get("from") || undefined,
      to: params.get("to") || undefined,
      limit: PAGE_SIZE,
      skip: (page - 1) * PAGE_SIZE,
    }),
    [params, page]
  );

  const changes = useQuery({
    queryKey: ["b2b", "changes", "feed", filters],
    queryFn: () => api.b2b.changes(filters),
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next);
  }

  const total = changes.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Change log</h1>
        <p className="text-sm text-muted-foreground">
          {changes.data ? `${total} recorded changes` : "Loading…"}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setParam("actor", actorInput.trim());
          }}
        >
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="w-56 pl-8"
              placeholder="Who made it…"
              value={actorInput}
              onChange={(event) => setActorInput(event.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary">
            Filter
          </Button>
        </form>

        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={params.get("operation") ?? ""}
          onChange={(event) => setParam("operation", event.target.value)}
        >
          <option value="">All operations</option>
          {B2B_OPERATIONS.map((operation) => (
            <option key={operation} value={operation}>
              {operation}
            </option>
          ))}
        </select>

        {/* Isolating dry-run rows is the point of this filter: they are the ones that
            look like changes in an email but moved nothing. */}
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={params.get("mode") ?? ""}
          onChange={(event) => setParam("mode", event.target.value)}
        >
          <option value="">All modes</option>
          {MODES.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </select>

        <Input
          type="date"
          className="w-40"
          value={params.get("from") ?? ""}
          onChange={(event) => setParam("from", event.target.value)}
        />
        <Input
          type="date"
          className="w-40"
          value={params.get("to") ?? ""}
          onChange={(event) => setParam("to", event.target.value)}
        />

        {[...params.keys()].length > 0 && (
          <Button variant="ghost" onClick={() => setParams(new URLSearchParams())}>
            Clear filters
          </Button>
        )}
      </div>

      <div className="rounded-xl border bg-card p-2">
        <ChangeList
          changes={changes.data?.items ?? []}
          loading={changes.isLoading}
          linkToRecord
          emptyMessage="No changes match these filters."
        />
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setParam("page", String(page - 1))}
          >
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {page} of {pages}
          </span>
          <Button
            variant="secondary"
            disabled={page >= pages}
            onClick={() => setParam("page", String(page + 1))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
